/**
 * GeoTIFF Viewer Component
 * Enhanced viewer with Cesium for proper georeferencing:
 * - Layer controls with opacity
 * - DSM colorization
 * - Measurement tools (distance, area)
 * - Basemap options (satellite, streets)
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';

function useIsMobile(breakpoint = 700) {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= breakpoint);
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${breakpoint}px)`);
    const onChange = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [breakpoint]);
  return isMobile;
}
import { useLocation } from 'react-router-dom';
import * as Cesium from 'cesium';
import 'cesium/Build/Cesium/Widgets/widgets.css';

// ─── Camera helper: fit rectangle to ~90% of viewport ────────────────────────
// Computes camera height directly from the rectangle's Web Mercator extent.
// In Cesium 2D mode: frustum.right = cameraHeight * 0.5, so the visible
// width in projected meters equals cameraHeight.  We compute the height
// needed for the rectangle to fill the desired fraction of the viewport.
function fitCameraToRect(
  viewer: Cesium.Viewer,
  rectangle: Cesium.Rectangle,
  options?: { duration?: number }
) {
  const duration = options?.duration ?? 0;
  const fill = 1.1; // target: rectangle fills ~100% of viewport with slight overflow

  // Rectangle extent in Web Mercator metres
  const R = 6378137; // WGS-84 semi-major axis
  const xSpan = R * Math.abs(rectangle.east - rectangle.west);
  const ySpan = Math.abs(
    R * Math.log(Math.tan(Math.PI / 4 + rectangle.north / 2)) -
    R * Math.log(Math.tan(Math.PI / 4 + rectangle.south / 2))
  );

  // Viewport aspect ratio (fallback 16:9 if canvas not laid out yet)
  const canvas = viewer.scene.canvas;
  const vw = canvas.clientWidth || 1600;
  const vh = canvas.clientHeight || 900;

  // In Cesium 2D the visible extent is:
  //   width  = cameraHeight
  //   height = cameraHeight * (canvasH / canvasW)
  // Solve for cameraHeight so rectangle fills `fill` fraction:
  const hForWidth = xSpan / fill;
  const hForHeight = (ySpan / fill) * (vw / vh);
  const cameraHeight = Math.max(hForWidth, hForHeight);

  const centerLon = (rectangle.west + rectangle.east) / 2;
  const centerLat = (rectangle.south + rectangle.north) / 2;
  const destination = Cesium.Cartesian3.fromRadians(centerLon, centerLat, cameraHeight);

  if (duration > 0) {
    viewer.camera.flyTo({ destination, duration });
  } else {
    viewer.camera.setView({ destination });
  }
  viewer.scene.requestRender();
}

import { useApp } from '../../store/AppContext';
import type { TileMetadata, PhotoData, PhotoCategory } from '../../../shared/types/project';
import { DEFAULT_PHOTO_CATEGORIES } from '../../../shared/types/project';
import type { FloorPlanOverlay, FloorPlanTransform } from '../../../shared/types/floorplan';
import { calculateBoundsFromTransform } from '../../../shared/types/floorplan';
import { AddPhotoModal } from '../modals/AddPhotoModal';
import { AddFloorPlanModal } from '../modals/AddFloorPlanModal';
import { CategoryManagerModal } from '../modals/CategoryManagerModal';
import { PhotoPreviewModal } from '../modals/PhotoPreviewModal';
import { PhotoPanel } from './PhotoPanel';
import { PhotoSidebar } from './PhotoSidebar';
import { FloorPlanAligner } from '../tools/FloorPlanAligner';
import { LeftSidebar, type MediaType, type MediaTypeConfig, type OverlayItem } from '../sidebar/LeftSidebar';
import { RightInfoPanel } from '../sidebar/RightInfoPanel';
import { ClusterLevel, clusterLevelFromHeight, clusterPhotos, type PhotoCluster } from '../../utils/photoClusterEngine';
import {
  createAggregateMarkerSvg,
  createCategoryMarkerSvg,
  createIndividualMarkerSvg,
} from '../../utils/markerSvgFactory';
import type { DataMode, MeasureMode, Basemap, Measurement } from '../../features/geotiff/types';
import { SetupView } from '../../features/geotiff/components/SetupView';
import { createBoundedImageryProvider } from '../../utils/boundedImageryProvider';
import { patchCesiumSkeletons } from '../../utils/patchCesiumSkeletons';
import './ViewerCommon.css';

// Apply Cesium bug fix before any Viewer is created
patchCesiumSkeletons();
import './GeoTiffViewer.css';

// Size threshold for tiling (100MB)

// Stable empty arrays to prevent unnecessary re-renders
const EMPTY_PHOTOS: PhotoData[] = [];
const EMPTY_CATEGORIES: PhotoCategory[] = [];

export function GeoTiffViewer() {
  const { state, updateProject, setStatus } = useApp();
  const isPortal = useLocation().pathname.startsWith('/portal');
  const isMobile = useIsMobile(700);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [cursorInfo, setCursorInfo] = useState<{ lng: number; lat: number; x?: number; y?: number } | null>(null);
  const [dataMode, setDataMode] = useState<DataMode>('ortho');
  const [_tileMetadata, setTileMetadata] = useState<TileMetadata | null>(null);
  const [serverPort, setServerPort] = useState<number>(0);

  // Layer controls
  const [orthoOpacity, setOrthoOpacity] = useState(1);
  const [dsmOpacity, setDsmOpacity] = useState(0.7);
  const [showOrtho, setShowOrtho] = useState(true);
  const [showDsm, setShowDsm] = useState(false);
  // Refs to avoid re-creating loadTileLayer on every opacity/visibility change
  const orthoOpacityRef = useRef(orthoOpacity);
  const dsmOpacityRef = useRef(dsmOpacity);
  const showOrthoRef = useRef(showOrtho);
  const showDsmRef = useRef(showDsm);
  orthoOpacityRef.current = orthoOpacity;
  dsmOpacityRef.current = dsmOpacity;
  showOrthoRef.current = showOrtho;
  showDsmRef.current = showDsm;
  const [basemap, setBasemap] = useState<Basemap>('satellite');
  const [measureMode, setMeasureMode] = useState<'none' | 'distance' | 'area'>('none');
  const [measurements, setMeasurements] = useState<Measurement[]>([]);
  const [measurePoints, setMeasurePoints] = useState<Cesium.Cartesian3[]>([]);

  // Photo state
  const [showPhotoPanel, setShowPhotoPanel] = useState(true);
  const [selectedPhotoId, setSelectedPhotoId] = useState<string | undefined>();
  const [addPhotoModalOpen, setAddPhotoModalOpen] = useState(false);
  const [addPhotoModalCoordinates, setAddPhotoModalCoordinates] = useState<{ longitude: number; latitude: number } | undefined>();
  const [editingPhoto, setEditingPhoto] = useState<PhotoData | null>(null);
  const [categoryManagerOpen, setCategoryManagerOpen] = useState(false);
  const [placingPhotoMode, setPlacingPhotoMode] = useState(false);
  const [pendingPhotoData, setPendingPhotoData] = useState<Omit<PhotoData, 'id' | 'addedAt' | 'coordinates'> | null>(null);
  const [photoPreviewOpen, setPhotoPreviewOpen] = useState(false);
  const [previewPhotos, setPreviewPhotos] = useState<PhotoData[]>([]);
  const [previewInitialPhotoId, setPreviewInitialPhotoId] = useState<string | undefined>();

  // Photo sidebar state (inline viewer for map marker clicks)
  const [photoSidebarOpen, setPhotoSidebarOpen] = useState(false);
  const [sidebarPhotos, setSidebarPhotos] = useState<PhotoData[]>([]);
  const [sidebarInitialPhotoId, setSidebarInitialPhotoId] = useState<string | undefined>();
  // Counter to force sidebar to re-process initialPhotoId even when the
  // same photo is clicked twice (e.g. clicking the same cluster marker)
  const [sidebarNavCounter, setSidebarNavCounter] = useState(0);

  // Cluster level for zoom-dependent photo grouping
  const [clusterLevel, setClusterLevel] = useState<ClusterLevel>(ClusterLevel.INDIVIDUAL);
  const clusterLevelRef = useRef<ClusterLevel>(ClusterLevel.INDIVIDUAL);

  // Layer panel state (simple right-side panel)
  const [showLayerPanel, setShowLayerPanel] = useState(true);

  // Media type visibility state
  const [visibleMediaTypes, setVisibleMediaTypes] = useState<Set<string>>(new Set(['photos']));

  // Floor plan state
  const [floorPlanModalOpen, setFloorPlanModalOpen] = useState(false);
  const [aligningFloorPlan, setAligningFloorPlan] = useState<FloorPlanOverlay | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const viewerRef = useRef<Cesium.Viewer | null>(null);
  const loadedSourceRef = useRef<string | null>(null);
  const orthoLayerRef = useRef<Cesium.ImageryLayer | null>(null);
  const dsmLayerRef = useRef<Cesium.ImageryLayer | null>(null);
  const measureEntitiesRef = useRef<Cesium.Entity[]>([]);
  const measureModeRef = useRef<MeasureMode>('none');
  const measurePointsRef = useRef<Cesium.Cartesian3[]>([]);
  const handlerRef = useRef<Cesium.ScreenSpaceEventHandler | null>(null);
  const photoEntitiesRef = useRef<Map<string, Cesium.Entity>>(new Map());
  const placingPhotoModeRef = useRef(false);
  const pendingPhotoDataRef = useRef<Omit<PhotoData, 'id' | 'addedAt' | 'coordinates'> | null>(null);
  const handleAddPhotoRef = useRef<((photoData: Omit<PhotoData, 'id' | 'addedAt'>) => Promise<void>) | null>(null);
  const handleUpdatePhotoRef = useRef<((photo: PhotoData) => Promise<void>) | null>(null);
  const handleFlyToPhotoRef = useRef<((photo: PhotoData) => void) | null>(null);
  const floorPlanLayersRef = useRef<Map<string, Cesium.ImageryLayer>>(new Map());

  const project = state.currentProject;
  const geotiff = project?.geotiff;

  // Determine what data sources are available
  const hasOrtho = geotiff?.orthoPath || geotiff?.orthoTilesDir || geotiff?.orthoTileCache;
  const hasDsm = geotiff?.dsmPath || geotiff?.dsmTilesDir || geotiff?.dsmTileCache;
  const hasData = hasOrtho || hasDsm;

  // Photo data - use stable empty arrays to prevent unnecessary re-renders
  const photos = geotiff?.photos || EMPTY_PHOTOS;
  const customCategories = geotiff?.customCategories || EMPTY_CATEGORIES;

  // Ref for photos — used by click handler to avoid stale closures
  const photosRef = useRef<PhotoData[]>(photos);
  useEffect(() => {
    photosRef.current = photos;
  }, [photos]);

  // Floor plan data
  const floorPlans = geotiff?.floorPlans?.overlays || [];
  const allCategories = useMemo(
    () => [...DEFAULT_PHOTO_CATEGORIES, ...customCategories],
    [customCategories]
  );

  // Debug: Log when photos change
  useEffect(() => {
    console.log('[GeoTiffViewer] Photos state changed:', photos.length, 'photos');
    if (photos.length > 0) {
      console.log('[GeoTiffViewer] First photo:', photos[0]);
    }
  }, [photos]);

  const getCategoryById = useCallback(
    (id: string): PhotoCategory | undefined => {
      return allCategories.find(c => c.id === id);
    },
    [allCategories]
  );

  // Get current tile cache hash and metadata
  const orthoTileCache = geotiff?.orthoTileCache;
  const orthoTileMetadata = geotiff?.orthoTileMetadata;
  const dsmTileCache = geotiff?.dsmTileCache;
  const dsmTileMetadata = geotiff?.dsmTileMetadata;
  const currentPath = dataMode === 'ortho' ? geotiff?.orthoPath : geotiff?.dsmPath;
  const positionOffset = geotiff?.positionOffset || { lng: 0, lat: 0 };

  // Set server port - API is served from the same origin
  useEffect(() => {
    // Tiles are served from /api/tiles/* on the same origin
    // Use port from window.location or default
    const port = window.location.port ? parseInt(window.location.port) : 80;
    setServerPort(port);
  }, []);

  // Initialize Cesium viewer
  useEffect(() => {
    if (!containerRef.current || viewerRef.current) return;

    // Disable Cesium Ion — we use Mapbox for basemaps, not Ion
    Cesium.Ion.defaultAccessToken = undefined as unknown as string;

    // Create viewer with no default imagery (baseLayer: false prevents Ion fetch)
    const viewer = new Cesium.Viewer(containerRef.current, {
      baseLayerPicker: false,
      geocoder: false,
      homeButton: false,
      infoBox: false,
      navigationHelpButton: false,
      // @ts-expect-error sceneModeLauncher exists at runtime but missing from types
      sceneModeLauncher: false,
      selectionIndicator: false,
      timeline: false,
      animation: false,
      fullscreenButton: false,
      vrButton: false,
      sceneMode: Cesium.SceneMode.SCENE2D,
      mapProjection: new Cesium.WebMercatorProjection(),
      baseLayer: false,
      terrainProvider: new Cesium.EllipsoidTerrainProvider(),
      requestRenderMode: true,
      maximumRenderTimeChange: Infinity,
    });

    // Hide Cesium logo / credits bar
    viewer.creditDisplay.container.style.display = 'none';

    // Disable default double-click zoom (interferes with measurement tool)
    viewer.screenSpaceEventHandler.removeInputAction(Cesium.ScreenSpaceEventType.LEFT_DOUBLE_CLICK);

    // Disable depth testing against terrain for better 2D performance
    viewer.scene.globe.depthTestAgainstTerrain = false;

    // Set dark background
    viewer.scene.backgroundColor = Cesium.Color.fromCssColorString('#1a1a1a');

    // Store viewer ref immediately
    viewerRef.current = viewer;

    // Set initial camera to tile bounds (deferred one frame so canvas is sized)
    const initialBounds = orthoTileMetadata?.boundsWgs84 || dsmTileMetadata?.boundsWgs84;
    if (initialBounds) {
      const offset = geotiff?.positionOffset || { lng: 0, lat: 0 };
      const rect = Cesium.Rectangle.fromDegrees(
        initialBounds.minX + offset.lng,
        initialBounds.minY + offset.lat,
        initialBounds.maxX + offset.lng,
        initialBounds.maxY + offset.lat
      );
      // Defer to next frame so the canvas has its final layout dimensions
      requestAnimationFrame(() => {
        if (viewer && !viewer.isDestroyed()) {
          fitCameraToRect(viewer, rect);
        }
      });
    } else if (project?.latitude != null && project?.longitude != null) {
      // No tile data — fly to project coordinates
      requestAnimationFrame(() => {
        if (viewer && !viewer.isDestroyed()) {
          viewer.camera.flyTo({
            destination: Cesium.Cartesian3.fromDegrees(project.longitude!, project.latitude!, 500),
            orientation: { heading: 0, pitch: Cesium.Math.toRadians(-60), roll: 0 },
            duration: 0,
          });
        }
      });
    } else if (project?.address || project?.city) {
      // No coordinates — geocode the address and fly there
      const query = [project.address, project.city, project.state].filter(Boolean).join(', ');
      const mapboxToken = import.meta.env.VITE_MAPBOX_TOKEN;
      if (query && mapboxToken) {
        fetch(`https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?access_token=${mapboxToken}&limit=1`)
          .then(r => r.json())
          .then(data => {
            const feature = data.features?.[0];
            if (feature && viewer && !viewer.isDestroyed()) {
              const [lng, lat] = feature.center;
              viewer.camera.flyTo({
                destination: Cesium.Cartesian3.fromDegrees(lng, lat, 500),
                orientation: { heading: 0, pitch: Cesium.Math.toRadians(-60), roll: 0 },
                duration: 0,
              });
            }
          })
          .catch(() => { /* geocoding failed, stay at default view */ });
      }
    }

    // Add base imagery after viewer is ready
    updateBasemap(viewer, basemap);

    // Track mouse position
    const handler = new Cesium.ScreenSpaceEventHandler(viewer.scene.canvas);

    handler.setInputAction((movement: Cesium.ScreenSpaceEventHandler.MotionEvent) => {
      const cartesian = viewer.camera.pickEllipsoid(movement.endPosition, viewer.scene.globe.ellipsoid);
      if (cartesian) {
        const cartographic = Cesium.Cartographic.fromCartesian(cartesian);
        const lng = Cesium.Math.toDegrees(cartographic.longitude);
        const lat = Cesium.Math.toDegrees(cartographic.latitude);

        const metadata = orthoTileMetadata || dsmTileMetadata;
        if (metadata?.boundsWgs84) {
          const bounds = metadata.boundsWgs84;
          const x = Math.floor(
            ((lng - bounds.minX) / (bounds.maxX - bounds.minX)) * metadata.originalWidth
          );
          const y = Math.floor(
            ((bounds.maxY - lat) / (bounds.maxY - bounds.minY)) * metadata.originalHeight
          );

          if (x >= 0 && x < metadata.originalWidth && y >= 0 && y < metadata.originalHeight) {
            setCursorInfo({ lng, lat, x, y });
          } else {
            setCursorInfo({ lng, lat });
          }
        } else {
          setCursorInfo({ lng, lat });
        }
      }
    }, Cesium.ScreenSpaceEventType.MOUSE_MOVE);

    // Unified LEFT_CLICK handler — photo markers, placing photos, and measurements.
    // All mutable state is accessed via refs to avoid stale closures.
    handler.setInputAction((click: Cesium.ScreenSpaceEventHandler.PositionedEvent) => {
      const currentPhotos = photosRef.current;

      // ── 1. Placing-photo mode: place at clicked location ──────────
      if (placingPhotoModeRef.current && pendingPhotoDataRef.current) {
        const cartesian = viewer.camera.pickEllipsoid(click.position, viewer.scene.globe.ellipsoid);
        if (cartesian) {
          const cartographic = Cesium.Cartographic.fromCartesian(cartesian);
          const lng = Cesium.Math.toDegrees(cartographic.longitude);
          const lat = Cesium.Math.toDegrees(cartographic.latitude);

          const pendingData = pendingPhotoDataRef.current as any;
          const editingId = pendingData?._editingId;
          const { _editingId, ...cleanData } = pendingData || {};

          const photoData = {
            ...cleanData,
            coordinates: { longitude: lng, latitude: lat },
            isManuallyPlaced: true,
          };

          setPlacingPhotoMode(false);
          setPendingPhotoData(null);

          if (editingId && handleUpdatePhotoRef.current) {
            const existingPhoto = currentPhotos.find(p => p.id === editingId);
            if (existingPhoto) {
              handleUpdatePhotoRef.current({ ...existingPhoto, ...photoData });
            }
          } else if (handleAddPhotoRef.current) {
            handleAddPhotoRef.current(photoData);
          }
        }
        return;
      }

      // ── 2. Photo marker click: open inline sidebar ────────────────
      const pickedObject = viewer.scene.pick(click.position);
      if (Cesium.defined(pickedObject) && pickedObject.id) {
        const entity = pickedObject.id as Cesium.Entity;
        if (entity.properties?.type?.getValue() === 'photo') {
          const entityClusterLevel = entity.properties.clusterLevel?.getValue() as number | undefined;
          const photoIds = entity.properties.photoIds?.getValue() as string[] | undefined;
          const firstPhotoId = entity.properties.firstPhotoId?.getValue() as string;

          // AGGREGATE marker: fly camera closer instead of opening sidebar
          if (entityClusterLevel === ClusterLevel.AGGREGATE && firstPhotoId) {
            const photo = currentPhotos.find(p => p.id === firstPhotoId);
            if (photo) {
              viewer.camera.flyTo({
                destination: Cesium.Cartesian3.fromDegrees(
                  photo.coordinates.longitude,
                  photo.coordinates.latitude,
                  2000,
                ),
                duration: 0.6,
              });
            }
            return;
          }

          // CATEGORY / INDIVIDUAL marker: open inline photo sidebar
          if (photoIds && photoIds.length > 0) {
            const clusterPhotosForSidebar = photoIds
              .map(id => currentPhotos.find(p => p.id === id))
              .filter((p): p is PhotoData => !!p);

            if (clusterPhotosForSidebar.length > 0) {
              setSelectedPhotoId(firstPhotoId);
              setSidebarPhotos(clusterPhotosForSidebar);
              setSidebarInitialPhotoId(firstPhotoId);
              setSidebarNavCounter(c => c + 1);
              setPhotoSidebarOpen(true);
            }
          }
          return;
        }
      }

      // ── 3. Measurement click: add point if in measure mode ────────
      if (measureModeRef.current !== 'none') {
        const cartesian = viewer.camera.pickEllipsoid(click.position, viewer.scene.globe.ellipsoid);
        if (!cartesian) return;

        setMeasurePoints(prev => [...prev, cartesian]);

        const entity = viewer.entities.add({
          position: cartesian,
          point: {
            pixelSize: 10,
            color: Cesium.Color.fromCssColorString('#ff6432'),
            outlineColor: Cesium.Color.WHITE,
            outlineWidth: 2,
          },
        });
        measureEntitiesRef.current.push(entity);

        const prevPoints = measurePointsRef.current;
        if (prevPoints.length > 0) {
          const lineEntity = viewer.entities.add({
            polyline: {
              positions: [prevPoints[prevPoints.length - 1], cartesian],
              width: 3,
              material: Cesium.Color.fromCssColorString('#ff6432'),
              clampToGround: true,
            },
          });
          measureEntitiesRef.current.push(lineEntity);
        }

        viewer.scene.requestRender();
      }
    }, Cesium.ScreenSpaceEventType.LEFT_CLICK);

    // Double-click to finish measurement
    handler.setInputAction(() => {
      if (measureModeRef.current !== 'none' && measurePointsRef.current.length >= 2) {
        finishMeasurement();
      }
    }, Cesium.ScreenSpaceEventType.LEFT_DOUBLE_CLICK);

    handlerRef.current = handler;
    console.log('[GeoTiffViewer] Cesium initialized');

    return () => {
      // Set ref to null first to prevent race conditions
      viewerRef.current = null;
      orthoLayerRef.current = null;
      dsmLayerRef.current = null;
      handler.destroy();
      handlerRef.current = null;
      measureEntitiesRef.current = [];
      viewer.destroy();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasData]); // Re-run when hasData changes (container becomes available)

  // Update basemap
  const updateBasemap = useCallback(async (viewer: Cesium.Viewer, basemapType: Basemap) => {
    // Check if viewer is still valid
    if (!viewer || viewer.isDestroyed()) {
      return;
    }

    // Remove existing base layers (not our ortho/dsm layers)
    const layersToRemove: Cesium.ImageryLayer[] = [];
    for (let i = 0; i < viewer.imageryLayers.length; i++) {
      const layer = viewer.imageryLayers.get(i);
      if (layer !== orthoLayerRef.current && layer !== dsmLayerRef.current) {
        layersToRemove.push(layer);
      }
    }
    layersToRemove.forEach(layer => viewer.imageryLayers.remove(layer, false));

    if (basemapType === 'none') {
      viewer.scene.backgroundColor = Cesium.Color.BLACK;
      viewer.scene.globe.baseColor = Cesium.Color.BLACK;
      viewer.scene.requestRender();
      return;
    }

    // Restore dark background for non-none basemaps
    viewer.scene.backgroundColor = Cesium.Color.fromCssColorString('#1a1a1a');

    try {
      let imageryProvider: Cesium.ImageryProvider;
      const mapboxToken = import.meta.env.VITE_MAPBOX_TOKEN;

      if (basemapType === 'satellite') {
        if (mapboxToken) {
          imageryProvider = new Cesium.UrlTemplateImageryProvider({
            url: `https://api.mapbox.com/v4/mapbox.satellite/{z}/{x}/{y}@2x.jpg90?access_token=${mapboxToken}`,
            maximumLevel: 22,
            tileWidth: 512,
            tileHeight: 512,
          });
        } else {
          imageryProvider = await Cesium.ArcGisMapServerImageryProvider.fromUrl(
            'https://services.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer'
          );
        }
      } else if (basemapType === 'hybrid') {
        if (mapboxToken) {
          // Mapbox satellite-streets: satellite imagery with road labels
          imageryProvider = new Cesium.UrlTemplateImageryProvider({
            url: `https://api.mapbox.com/styles/v1/mapbox/satellite-streets-v12/tiles/512/{z}/{x}/{y}@2x?access_token=${mapboxToken}`,
            maximumLevel: 22,
            tileWidth: 512,
            tileHeight: 512,
          });
        } else {
          imageryProvider = await Cesium.ArcGisMapServerImageryProvider.fromUrl(
            'https://services.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer'
          );
        }
      } else {
        // Streets
        if (mapboxToken) {
          imageryProvider = new Cesium.UrlTemplateImageryProvider({
            url: `https://api.mapbox.com/styles/v1/mapbox/streets-v12/tiles/512/{z}/{x}/{y}@2x?access_token=${mapboxToken}`,
            maximumLevel: 22,
            tileWidth: 512,
            tileHeight: 512,
          });
        } else {
          imageryProvider = new Cesium.OpenStreetMapImageryProvider({
            url: 'https://a.tile.openstreetmap.org/',
          });
        }
      }

      // Check again if viewer is still valid after async operation
      if (viewer.isDestroyed()) {
        return;
      }

      const baseLayer = viewer.imageryLayers.addImageryProvider(imageryProvider, 0);
      baseLayer.alpha = 1.0;
    } catch (error) {
      console.error('[GeoTiffViewer] Failed to load basemap:', error);
    }
  }, []);

  // Handle basemap change
  useEffect(() => {
    const viewer = viewerRef.current;
    if (!viewer) return;

    updateBasemap(viewer, basemap);
  }, [basemap, updateBasemap]);

  // Zoom listener — update cluster level when camera height changes
  useEffect(() => {
    const viewer = viewerRef.current;
    if (!viewer) return;

    const onCameraChanged = () => {
      const height = viewer.camera.positionCartographic.height;
      const newLevel = clusterLevelFromHeight(height);
      if (newLevel !== clusterLevelRef.current) {
        clusterLevelRef.current = newLevel;
        setClusterLevel(newLevel);
      }
    };

    viewer.camera.changed.addEventListener(onCameraChanged);
    // Also do an initial check
    onCameraChanged();

    return () => {
      if (!viewer.isDestroyed()) {
        viewer.camera.changed.removeEventListener(onCameraChanged);
      }
    };
  }, [hasData]); // re-bind when viewer is re-created

  // Load tile layers when we have tiles
  useEffect(() => {
    if (!viewerRef.current) {
      console.log('[GeoTiffViewer] Viewer not ready');
      return;
    }
    if (!serverPort) {
      console.log('[GeoTiffViewer] Server port not ready');
      return;
    }

    console.log('[GeoTiffViewer] useEffect - orthoTileCache:', orthoTileCache, 'currentPath:', currentPath, 'loadedSourceRef:', loadedSourceRef.current);

    // Load ortho if available and has metadata
    if (orthoTileCache && orthoTileMetadata) {
      setTileMetadata(orthoTileMetadata);
      loadTileLayer('ortho', orthoTileCache, orthoTileMetadata);
      loadedSourceRef.current = geotiff?.orthoPath || orthoTileCache;
    }

    // Load DSM if available
    if (dsmTileCache && dsmTileMetadata) {
      loadTileLayer('dsm', dsmTileCache, dsmTileMetadata);
    }
  }, [orthoTileCache, orthoTileMetadata, dsmTileCache, dsmTileMetadata, serverPort, geotiff?.orthoPath, dataMode]);

  // Load tile layer into Cesium
  const loadTileLayer = useCallback(async (layerId: 'ortho' | 'dsm', cacheHash: string, metadata: TileMetadata) => {
    const viewer = viewerRef.current;
    if (!viewer || !serverPort) {
      console.log(`[GeoTiffViewer] Cannot load ${layerId} - viewer or port not ready`);
      return;
    }

    // Check if we have WGS84 bounds for georeferencing
    if (!metadata.boundsWgs84) {
      console.error(`[GeoTiffViewer] No WGS84 bounds for ${layerId} - cannot georeference`);
      setStatus(`Cannot load ${layerId}: No geographic bounds available. Please re-process the tiles.`);
      return;
    }

    console.log(`[GeoTiffViewer] Loading tile layer: ${layerId}, hash: ${cacheHash}`);
    console.log(`[GeoTiffViewer] WGS84 bounds:`, metadata.boundsWgs84);
    console.log(`[GeoTiffViewer] Tile scheme:`, metadata.isGlobalScheme ? 'Global Web Mercator' : 'Local Pyramid');

    try {
      // Remove existing layer if present
      const existingLayer = layerId === 'ortho' ? orthoLayerRef.current : dsmLayerRef.current;
      if (existingLayer) {
        viewer.imageryLayers.remove(existingLayer, false);
      }

      // Create imagery provider with geographic bounds (applying position offset)
      const bounds = metadata.boundsWgs84;
      const rectangle = Cesium.Rectangle.fromDegrees(
        bounds.minX + positionOffset.lng,
        bounds.minY + positionOffset.lat,
        bounds.maxX + positionOffset.lng,
        bounds.maxY + positionOffset.lat
      );

      let imageryProvider: Cesium.UrlTemplateImageryProvider;

      // Check if this is a global Web Mercator tile scheme (like MipMap tiles)
      if (metadata.isGlobalScheme && metadata.minZoom !== undefined && metadata.maxZoom !== undefined) {
        // Global Web Mercator tiles — use bounded provider that returns
        // transparent blanks for z < minZoom, preventing 404s entirely.
        console.log(`[GeoTiffViewer] Using Global Web Mercator scheme: z=${metadata.minZoom}-${metadata.maxZoom}`);

        const tileUrl = `/api/tiles/${cacheHash}/{z}/{x}/{y}.${metadata.format}`;

        imageryProvider = createBoundedImageryProvider(
          tileUrl,
          metadata.minZoom,
          metadata.maxZoom,
          rectangle,
          metadata.tileSize,
        );
      } else {
        // Local pyramid tiles - use custom tiling scheme
        console.log(`[GeoTiffViewer] Using Local Pyramid scheme: ${metadata.levels} levels`);

        const tilingScheme = new Cesium.GeographicTilingScheme({
          rectangle: rectangle,
          numberOfLevelZeroTilesX: 1,
          numberOfLevelZeroTilesY: 1,
        });

        const directTileUrl = `/api/tiles/${cacheHash}/{z}/{x}/{y}.${metadata.format}?direct=true`;

        imageryProvider = new Cesium.UrlTemplateImageryProvider({
          url: directTileUrl,
          tilingScheme: tilingScheme,
          minimumLevel: 0,
          maximumLevel: metadata.levels - 1,
          tileWidth: metadata.tileSize,
          tileHeight: metadata.tileSize,
          credit: 'CoreSkan',
        });
      }

      // Suppress expected 404 errors for tiles outside data coverage.
      // When using WebMercatorTilingScheme with a limited rectangle, Cesium
      // still requests low-zoom tiles (z=0, z=1) that won't exist in the cache.
      imageryProvider.errorEvent.addEventListener((err: Cesium.TileProviderError) => {
        // Mark as handled so Cesium doesn't retry or log a noisy warning
        if (err.error && (err.error as any).statusCode === 404) {
          err.retry = false;
        }
        // Also suppress generic network errors from missing tiles
        err.retry = false;
      });

      // Add the imagery layer.  The rectangle constraint is on the PROVIDER
      // (not the layer) to prevent global tile enumeration.  Setting rectangle
      // on both the provider AND layer causes a Cesium 1.137 bug where
      // _createTileImagerySkeletons crashes with "Cannot read properties of
      // undefined (reading 'west')" due to a missing null check on the initial
      // clippedImageryRectangle in the WebMercator code path.
      const layer = new Cesium.ImageryLayer(imageryProvider);
      layer.alpha = layerId === 'ortho' ? orthoOpacityRef.current : dsmOpacityRef.current;
      layer.show = layerId === 'ortho' ? showOrthoRef.current : showDsmRef.current;
      viewer.imageryLayers.add(layer);

      // Store reference
      if (layerId === 'ortho') {
        orthoLayerRef.current = layer;
      } else {
        dsmLayerRef.current = layer;
      }

      // Fly to bounds on first load
      if (layerId === 'ortho' || !orthoTileCache) {
        fitCameraToRect(viewer, rectangle);
      }

      console.log(`[GeoTiffViewer] Loaded ${layerId} tile layer:`, cacheHash);
    } catch (error) {
      console.error(`Failed to load ${layerId} tile layer:`, error);
      setStatus(`Failed to load ${layerId} tiles: ${(error as Error).message}`);
    }
  }, [serverPort, orthoTileCache, setStatus, positionOffset]);

  // Update layer visibility and opacity
  useEffect(() => {
    let changed = false;
    if (orthoLayerRef.current) {
      orthoLayerRef.current.show = showOrtho;
      orthoLayerRef.current.alpha = orthoOpacity;
      changed = true;
    }
    if (dsmLayerRef.current) {
      dsmLayerRef.current.show = showDsm;
      dsmLayerRef.current.alpha = dsmOpacity;
      changed = true;
    }
    // Cesium is in requestRenderMode — explicitly request a frame so the
    // opacity/visibility change shows immediately without waiting for input.
    if (changed) {
      viewerRef.current?.scene.requestRender();
    }
  }, [showOrtho, showDsm, orthoOpacity, dsmOpacity]);

  // Keep refs in sync with state
  useEffect(() => {
    measureModeRef.current = measureMode;
  }, [measureMode]);

  useEffect(() => {
    measurePointsRef.current = measurePoints;
  }, [measurePoints]);

  // Measurement functions
  const startMeasurement = (mode: MeasureMode) => {
    setMeasureMode(mode);
    setMeasurePoints([]);
    clearMeasureMarkers();
  };

  const clearMeasureMarkers = () => {
    const viewer = viewerRef.current;
    if (viewer) {
      measureEntitiesRef.current.forEach(e => viewer.entities.remove(e));
    }
    measureEntitiesRef.current = [];
  };

  const finishMeasurement = useCallback(() => {
    const points = measurePointsRef.current;
    const mode = measureModeRef.current;
    const viewer = viewerRef.current;

    if (points.length < 2 || !viewer) {
      setMeasureMode('none');
      setMeasurePoints([]);
      clearMeasureMarkers();
      return;
    }

    // Convert Cartesian3 to [lng, lat] for storage
    const lngLatPoints: [number, number][] = points.map(cartesian => {
      const cartographic = Cesium.Cartographic.fromCartesian(cartesian);
      return [
        Cesium.Math.toDegrees(cartographic.longitude),
        Cesium.Math.toDegrees(cartographic.latitude)
      ];
    });

    let value = 0;
    if (mode === 'distance') {
      for (let i = 1; i < points.length; i++) {
        value += Cesium.Cartesian3.distance(points[i - 1], points[i]);
      }
    } else if (mode === 'area' && points.length >= 3) {
      value = calculateArea(lngLatPoints);
    }

    const measurement: Measurement = {
      id: `m-${Date.now()}`,
      type: mode as 'distance' | 'area',
      points: lngLatPoints,
      value,
    };

    setMeasurements(prev => [...prev, measurement]);
    setMeasureMode('none');
    setMeasurePoints([]);
    clearMeasureMarkers();

    // Draw the measurement
    drawMeasurement(measurement);
  }, []);

  const calculateArea = (points: [number, number][]): number => {
    if (points.length < 3) return 0;

    // For small areas, use simple planar approximation with lat correction
    const centerLat = points.reduce((sum, p) => sum + p[1], 0) / points.length;
    const latCorrection = Math.cos(centerLat * Math.PI / 180);
    const metersPerDegreeLng = 111320 * latCorrection;
    const metersPerDegreeLat = 111320;

    const meterPoints = points.map(p => [
      (p[0] - points[0][0]) * metersPerDegreeLng,
      (p[1] - points[0][1]) * metersPerDegreeLat
    ]);

    let area = 0;
    for (let i = 0; i < meterPoints.length; i++) {
      const j = (i + 1) % meterPoints.length;
      area += meterPoints[i][0] * meterPoints[j][1];
      area -= meterPoints[j][0] * meterPoints[i][1];
    }

    return Math.abs(area / 2);
  };

  const drawMeasurement = (measurement: Measurement) => {
    const viewer = viewerRef.current;
    if (!viewer) return;

    const positions = measurement.points.map(p =>
      Cesium.Cartesian3.fromDegrees(p[0], p[1])
    );

    if (measurement.type === 'area') {
      // Close the polygon
      positions.push(positions[0]);
    }

    const entity = viewer.entities.add({
      polyline: {
        positions: positions,
        width: 3,
        material: Cesium.Color.fromCssColorString('#ff6432'),
        clampToGround: true,
      },
    });
    measureEntitiesRef.current.push(entity);

    if (measurement.type === 'area') {
      const polygonEntity = viewer.entities.add({
        polygon: {
          hierarchy: new Cesium.PolygonHierarchy(
            measurement.points.map(p => Cesium.Cartesian3.fromDegrees(p[0], p[1]))
          ),
          material: Cesium.Color.fromCssColorString('#ff6432').withAlpha(0.2),
          outline: false,
        },
      });
      measureEntitiesRef.current.push(polygonEntity);
    }
  };

  const clearAllMeasurements = () => {
    const viewer = viewerRef.current;
    if (viewer) {
      measureEntitiesRef.current.forEach(e => viewer.entities.remove(e));
      measureEntitiesRef.current = [];
    }
    setMeasurements([]);
  };

  // Note: handlePositionOffsetChange removed - not used in simple layer panel

  // Fit view to imagery
  const handleFitToView = useCallback(() => {
    const viewer = viewerRef.current;
    const metadata = orthoTileMetadata || dsmTileMetadata;
    if (!viewer || !metadata?.boundsWgs84) return;

    const bounds = metadata.boundsWgs84;
    const rectangle = Cesium.Rectangle.fromDegrees(
      bounds.minX + positionOffset.lng,
      bounds.minY + positionOffset.lat,
      bounds.maxX + positionOffset.lng,
      bounds.maxY + positionOffset.lat
    );

    fitCameraToRect(viewer, rectangle, { duration: 0.5 });
  }, [orthoTileMetadata, dsmTileMetadata, positionOffset]);

  // ============================================
  // Photo Functions
  // ============================================

  // Multi-level zoom-dependent clustering
  const photoClusters = useMemo(
    () => clusterPhotos(photos, clusterLevel),
    [photos, clusterLevel],
  );

  // Open photo preview for a photo or group (used by PhotoPanel list double-clicks)
  const openPhotoPreview = useCallback((photo: PhotoData) => {
    // Find the cluster this photo belongs to
    let groupPhotos: PhotoData[] = [photo];
    for (const cluster of photoClusters) {
      if (cluster.photos.some(p => p.id === photo.id)) {
        groupPhotos = cluster.photos;
        break;
      }
    }

    setPreviewPhotos(groupPhotos);
    setPreviewInitialPhotoId(photo.id);
    setPhotoPreviewOpen(true);
  }, [photoClusters]);

  // Create marker icon for a cluster — delegates to factory based on cluster level
  const createClusterMarkerIcon = useCallback(
    (cluster: PhotoCluster, isSelected: boolean): string => {
      const count = cluster.photos.length;
      switch (cluster.level) {
        case ClusterLevel.AGGREGATE:
          return createAggregateMarkerSvg(count, isSelected);
        case ClusterLevel.CATEGORY: {
          const cat = getCategoryById(cluster.categoryId || cluster.photos[0].categoryId) || DEFAULT_PHOTO_CATEGORIES[0];
          return createCategoryMarkerSvg(cat, count, isSelected);
        }
        case ClusterLevel.INDIVIDUAL:
        default: {
          const cat = getCategoryById(cluster.photos[0].categoryId) || DEFAULT_PHOTO_CATEGORIES[0];
          return createIndividualMarkerSvg(cat, count, isSelected);
        }
      }
    },
    [getCategoryById],
  );

  // Render photo markers on the map (re-runs when clusters change)
  const renderPhotoMarkers = useCallback(() => {
    const viewer = viewerRef.current;
    if (!viewer) return;

    // Clear existing photo markers
    photoEntitiesRef.current.forEach((entity) => {
      viewer.entities.remove(entity);
    });
    photoEntitiesRef.current.clear();

    // Add markers for each cluster
    for (const cluster of photoClusters) {
      const firstPhoto = cluster.photos[0];

      try {
        const entity = viewer.entities.add({
          id: `photo-cluster-${cluster.id}`,
          position: Cesium.Cartesian3.fromDegrees(
            cluster.center.longitude,
            cluster.center.latitude,
            0,
          ),
          billboard: {
            image: createClusterMarkerIcon(cluster, false),
            verticalOrigin: Cesium.VerticalOrigin.CENTER,
            horizontalOrigin: Cesium.HorizontalOrigin.CENTER,
            disableDepthTestDistance: Number.POSITIVE_INFINITY,
            scale: 1.0,
          },
          properties: {
            photoIds: cluster.photos.map(p => p.id),
            firstPhotoId: firstPhoto.id,
            groupCount: cluster.photos.length,
            type: 'photo',
            categoryId: cluster.categoryId || firstPhoto.categoryId,
            clusterLevel: cluster.level,
          },
        });

        photoEntitiesRef.current.set(cluster.id, entity);
      } catch (error) {
        console.error('[GeoTiffViewer] Failed to add entity:', error);
      }
    }

    viewer.scene.requestRender();
  }, [photoClusters, createClusterMarkerIcon]);

  // Update markers when photos change
  useEffect(() => {
    renderPhotoMarkers();
  }, [renderPhotoMarkers]);

  // Update selected marker icon without recreating all markers
  useEffect(() => {
    const viewer = viewerRef.current;
    if (!viewer) return;

    for (const cluster of photoClusters) {
      const entity = photoEntitiesRef.current.get(cluster.id);
      if (entity && entity.billboard) {
        const isSelected = cluster.photos.some(p => p.id === selectedPhotoId);
        entity.billboard.image = new Cesium.ConstantProperty(
          createClusterMarkerIcon(cluster, isSelected),
        );
      }
    }

    viewer.scene.requestRender();
  }, [selectedPhotoId, photoClusters, createClusterMarkerIcon]);

  // Keep refs in sync with placing photo state
  useEffect(() => {
    console.log('[GeoTiffViewer] placingPhotoMode changed to:', placingPhotoMode);
    placingPhotoModeRef.current = placingPhotoMode;
  }, [placingPhotoMode]);

  useEffect(() => {
    pendingPhotoDataRef.current = pendingPhotoData;
  }, [pendingPhotoData]);

  // Ref to store openPhotoPreview function
  const openPhotoPreviewRef = useRef<((photo: PhotoData) => void) | null>(null);

  // Keep openPhotoPreview ref in sync
  useEffect(() => {
    openPhotoPreviewRef.current = openPhotoPreview;
  }, [openPhotoPreview]);

  // Photo click handler is now unified in the main Cesium init useEffect above.
  // This eliminates the second ScreenSpaceEventHandler that caused stale-closure
  // "one-click-behind" bugs when both handlers raced on LEFT_CLICK.

  // Cancel placing photo mode on escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (photoSidebarOpen) {
          setPhotoSidebarOpen(false);
          return;
        }
        if (placingPhotoMode) {
          setPlacingPhotoMode(false);
          setPendingPhotoData(null);
          setStatus('Photo placement cancelled');
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [placingPhotoMode, setStatus, photoSidebarOpen]);

  // Enter placing photo mode
  const handleEnterPlacingMode = useCallback((photoData: Omit<PhotoData, 'id' | 'addedAt' | 'coordinates'>) => {
    console.log('[GeoTiffViewer] Entering placing mode with photo data:', photoData);
    setPendingPhotoData(photoData);
    setPlacingPhotoMode(true);
    setStatus('Click on the map to place the photo');
  }, [setStatus]);

  // Add a new photo
  const handleAddPhoto = useCallback(async (photoData: Omit<PhotoData, 'id' | 'addedAt'>) => {
    if (!project) {
      console.error('[GeoTiffViewer] handleAddPhoto: No project');
      return;
    }

    console.log('[GeoTiffViewer] handleAddPhoto: Adding photo', photoData);

    const newPhoto: PhotoData = {
      ...photoData,
      id: `photo-${Date.now()}`,
      addedAt: new Date().toISOString(),
    };

    console.log('[GeoTiffViewer] New photo object:', newPhoto);

    const updatedPhotos = [...photos, newPhoto];
    console.log('[GeoTiffViewer] Total photos after add:', updatedPhotos.length);

    await updateProject(project.id, {
      geotiff: {
        ...geotiff,
        photos: updatedPhotos,
      },
    });

    setSelectedPhotoId(newPhoto.id);
    setStatus('Photo added');
    console.log('[GeoTiffViewer] Photo added successfully');
  }, [project, photos, geotiff, updateProject, setStatus]);

  // Keep handleAddPhoto ref in sync
  useEffect(() => {
    handleAddPhotoRef.current = handleAddPhoto;
  }, [handleAddPhoto]);

  // Update photo metadata
  const handleUpdatePhoto = useCallback(async (updatedPhoto: PhotoData) => {
    if (!project) return;

    const updatedPhotos = photos.map(p =>
      p.id === updatedPhoto.id ? updatedPhoto : p
    );

    await updateProject(project.id, {
      geotiff: {
        ...geotiff,
        photos: updatedPhotos,
      },
    });

    setEditingPhoto(null);
    setStatus('Photo updated');
  }, [project, photos, geotiff, updateProject, setStatus]);

  // Keep handleUpdatePhoto ref in sync
  useEffect(() => {
    handleUpdatePhotoRef.current = handleUpdatePhoto;
  }, [handleUpdatePhoto]);

  // Delete a photo
  const handleDeletePhoto = useCallback(async (photoId: string) => {
    if (!project) return;

    const updatedPhotos = photos.filter(p => p.id !== photoId);

    await updateProject(project.id, {
      geotiff: {
        ...geotiff,
        photos: updatedPhotos,
      },
    });

    if (selectedPhotoId === photoId) {
      setSelectedPhotoId(undefined);
    }

    setStatus('Photo deleted');
  }, [project, photos, geotiff, selectedPhotoId, updateProject, setStatus]);

  // Fly camera to photo location
  const handleFlyToPhoto = useCallback((photo: PhotoData) => {
    const viewer = viewerRef.current;
    if (!viewer) return;

    const destination = Cesium.Cartesian3.fromDegrees(
      photo.coordinates.longitude,
      photo.coordinates.latitude,
      1000 // altitude in meters
    );

    viewer.camera.flyTo({
      destination,
      duration: 0.5,
    });
  }, []);

  // Keep handleFlyToPhoto ref in sync
  useEffect(() => {
    handleFlyToPhotoRef.current = handleFlyToPhoto;
  }, [handleFlyToPhoto]);

  // Save custom categories
  const handleSaveCategories = useCallback(async (categories: PhotoCategory[]) => {
    if (!project) return;

    // Check if any photos need to be reassigned to 'general'
    const categoryIds = new Set(categories.map(c => c.id));
    const defaultIds = new Set(DEFAULT_PHOTO_CATEGORIES.map(c => c.id));
    const validIds = new Set([...categoryIds, ...defaultIds]);

    const updatedPhotos = photos.map(photo => {
      if (!validIds.has(photo.categoryId)) {
        return { ...photo, categoryId: 'general' };
      }
      return photo;
    });

    await updateProject(project.id, {
      geotiff: {
        ...geotiff,
        customCategories: categories,
        photos: updatedPhotos,
      },
    });

    setStatus('Categories updated');
  }, [project, photos, geotiff, updateProject, setStatus]);

  // Count photos using a category
  const photosUsingCategory = useCallback((categoryId: string): number => {
    return photos.filter(p => p.categoryId === categoryId).length;
  }, [photos]);

  // ============================================
  // Floor Plan Functions
  // ============================================

  // Get center coordinates for default floor plan placement
  const getMapCenter = useCallback((): { lng: number; lat: number } => {
    const viewer = viewerRef.current;
    if (viewer && !viewer.isDestroyed()) {
      const camera = viewer.camera;
      const cartographic = camera.positionCartographic;
      return {
        lng: Cesium.Math.toDegrees(cartographic.longitude),
        lat: Cesium.Math.toDegrees(cartographic.latitude),
      };
    }
    // Fallback to ortho/dsm bounds center if available
    const metadata = orthoTileMetadata || dsmTileMetadata;
    if (metadata?.boundsWgs84) {
      return {
        lng: (metadata.boundsWgs84.minX + metadata.boundsWgs84.maxX) / 2,
        lat: (metadata.boundsWgs84.minY + metadata.boundsWgs84.maxY) / 2,
      };
    }
    return { lng: 0, lat: 0 };
  }, [orthoTileMetadata, dsmTileMetadata]);

  // Add a new floor plan
  const handleAddFloorPlan = useCallback(async (overlay: Omit<FloorPlanOverlay, 'id' | 'createdAt' | 'updatedAt'>) => {
    if (!project) return;

    const now = new Date().toISOString();
    const newFloorPlan: FloorPlanOverlay = {
      ...overlay,
      id: `fp-${Date.now()}`,
      createdAt: now,
      updatedAt: now,
    };

    const updatedFloorPlans = [...floorPlans, newFloorPlan];

    await updateProject(project.id, {
      geotiff: {
        ...geotiff,
        floorPlans: { overlays: updatedFloorPlans },
      },
    });

    setStatus('Floor plan added');

    // Enter alignment mode for the new floor plan
    setAligningFloorPlan(newFloorPlan);
  }, [project, floorPlans, geotiff, updateProject, setStatus]);

  // Update floor plan transform after alignment
  const handleFloorPlanAlignmentConfirm = useCallback(async (transform: FloorPlanTransform) => {
    if (!project || !aligningFloorPlan) return;

    const updatedFloorPlans = floorPlans.map(fp =>
      fp.id === aligningFloorPlan.id
        ? { ...fp, transform, updatedAt: new Date().toISOString() }
        : fp
    );

    await updateProject(project.id, {
      geotiff: {
        ...geotiff,
        floorPlans: { overlays: updatedFloorPlans },
      },
    });

    setAligningFloorPlan(null);
    setStatus('Floor plan alignment saved');
  }, [project, floorPlans, geotiff, aligningFloorPlan, updateProject, setStatus]);

  // Cancel floor plan alignment
  const handleFloorPlanAlignmentCancel = useCallback(() => {
    setAligningFloorPlan(null);
  }, []);

  // Toggle floor plan visibility
  const handleFloorPlanVisibilityChange = useCallback(async (id: string, visible: boolean) => {
    if (!project) return;

    const updatedFloorPlans = floorPlans.map(fp =>
      fp.id === id ? { ...fp, visible, updatedAt: new Date().toISOString() } : fp
    );

    await updateProject(project.id, {
      geotiff: {
        ...geotiff,
        floorPlans: { overlays: updatedFloorPlans },
      },
    });

    // Update layer visibility directly
    const layer = floorPlanLayersRef.current.get(id);
    if (layer) {
      layer.show = visible;
      viewerRef.current?.scene.requestRender();
    }
  }, [project, floorPlans, geotiff, updateProject]);

  // Change floor plan opacity — update Cesium immediately, debounce API save
  const floorPlanOpacityTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleFloorPlanOpacityChange = useCallback((id: string, opacity: number) => {
    // Update the Cesium layer immediately for smooth visual feedback
    const layer = floorPlanLayersRef.current.get(id);
    if (layer) {
      layer.alpha = opacity;
      viewerRef.current?.scene.requestRender();
    }

    // Debounce the expensive API save (300ms after user stops dragging)
    if (floorPlanOpacityTimerRef.current) {
      clearTimeout(floorPlanOpacityTimerRef.current);
    }
    floorPlanOpacityTimerRef.current = setTimeout(() => {
      if (!project) return;
      const updatedFloorPlans = floorPlans.map(fp =>
        fp.id === id ? { ...fp, opacity, updatedAt: new Date().toISOString() } : fp
      );
      updateProject(project.id, {
        geotiff: {
          ...geotiff,
          floorPlans: { overlays: updatedFloorPlans },
        },
      });
    }, 300);
  }, [project, floorPlans, geotiff, updateProject]);

  // Edit floor plan alignment
  const handleFloorPlanEdit = useCallback((id: string) => {
    const fp = floorPlans.find(f => f.id === id);
    if (fp) {
      setAligningFloorPlan(fp);
    }
  }, [floorPlans]);

  // Delete floor plan
  const handleFloorPlanDelete = useCallback(async (id: string) => {
    if (!project) return;

    // Remove from Cesium
    const layer = floorPlanLayersRef.current.get(id);
    if (layer && viewerRef.current) {
      viewerRef.current.imageryLayers.remove(layer, true);
      floorPlanLayersRef.current.delete(id);
    }

    const updatedFloorPlans = floorPlans.filter(fp => fp.id !== id);

    await updateProject(project.id, {
      geotiff: {
        ...geotiff,
        floorPlans: { overlays: updatedFloorPlans },
      },
    });

    setStatus('Floor plan deleted');
  }, [project, floorPlans, geotiff, updateProject, setStatus]);

  // Load floor plan tile layer into Cesium
  const loadFloorPlanLayer = useCallback((floorPlan: FloorPlanOverlay) => {
    const viewer = viewerRef.current;
    if (!viewer || viewer.isDestroyed() || !serverPort) return;

    // Skip if no tile cache
    if (!floorPlan.tileCache || !floorPlan.tileMetadata) return;

    // Skip if already loaded
    if (floorPlanLayersRef.current.has(floorPlan.id)) return;

    // Calculate bounds from transform and image dimensions
    const imageWidth = floorPlan.imageWidth || floorPlan.tileMetadata.originalWidth;
    const imageHeight = floorPlan.imageHeight || floorPlan.tileMetadata.originalHeight;

    if (!imageWidth || !imageHeight) return;

    const bounds = calculateBoundsFromTransform(floorPlan.transform, imageWidth, imageHeight);

    const rectangle = Cesium.Rectangle.fromDegrees(
      bounds.west,
      bounds.south,
      bounds.east,
      bounds.north
    );

    try {
      // Create tiling scheme for this floor plan
      const tilingScheme = new Cesium.GeographicTilingScheme({
        rectangle: rectangle,
        numberOfLevelZeroTilesX: 1,
        numberOfLevelZeroTilesY: 1,
      });

      const tileUrl = `/api/tiles/${floorPlan.tileCache}/{z}/{x}/{y}.${floorPlan.tileMetadata.format}?direct=true`;

      const imageryProvider = new Cesium.UrlTemplateImageryProvider({
        url: tileUrl,
        tilingScheme: tilingScheme,
        minimumLevel: 0,
        maximumLevel: floorPlan.tileMetadata.levels - 1,
        tileWidth: floorPlan.tileMetadata.tileSize,
        tileHeight: floorPlan.tileMetadata.tileSize,
        credit: 'Floor Plan',
      });

      const layer = viewer.imageryLayers.addImageryProvider(imageryProvider);
      layer.alpha = floorPlan.opacity;
      layer.show = floorPlan.visible;

      floorPlanLayersRef.current.set(floorPlan.id, layer);
      console.log(`[GeoTiffViewer] Loaded floor plan layer: ${floorPlan.name}`);
    } catch (error) {
      console.error(`[GeoTiffViewer] Failed to load floor plan layer:`, error);
    }
  }, [serverPort]);

  // Load all floor plan layers when they change
  useEffect(() => {
    if (!viewerRef.current || !serverPort) return;

    // Load any floor plans that aren't loaded yet
    floorPlans.forEach(fp => {
      if (!floorPlanLayersRef.current.has(fp.id) && fp.tileCache) {
        loadFloorPlanLayer(fp);
      }
    });

    // Remove layers for deleted floor plans
    floorPlanLayersRef.current.forEach((layer, id) => {
      if (!floorPlans.some(fp => fp.id === id)) {
        viewerRef.current?.imageryLayers.remove(layer, true);
        floorPlanLayersRef.current.delete(id);
      }
    });
  }, [floorPlans, serverPort, loadFloorPlanLayer]);


  const handleClear = async () => {
    if (!project) return;

    const viewer = viewerRef.current;
    if (viewer) {
      if (orthoLayerRef.current) {
        viewer.imageryLayers.remove(orthoLayerRef.current, false);
        orthoLayerRef.current = null;
      }
      if (dsmLayerRef.current) {
        viewer.imageryLayers.remove(dsmLayerRef.current, false);
        dsmLayerRef.current = null;
      }
    }

    await updateProject(project.id, { geotiff: undefined });
    setTileMetadata(null);
    loadedSourceRef.current = null;
    clearAllMeasurements();
    setStatus('GeoTIFF data cleared');
  };

  // ============================================================
  // HOOKS SECTION - All hooks must be defined before any returns
  // ============================================================

  // Note: visibleMediaTypes state removed - using simple layer panel

  // Build overlay items from layer state
  const overlayItems: OverlayItem[] = useMemo(() => {
    const items: OverlayItem[] = [];
    if (hasOrtho && orthoTileCache) {
      items.push({ id: 'ortho', name: 'Orthomosaic', visible: showOrtho });
    }
    if (hasDsm && dsmTileCache) {
      items.push({ id: 'dsm', name: 'DSM', visible: showDsm });
    }
    return items;
  }, [hasOrtho, hasDsm, orthoTileCache, dsmTileCache, showOrtho, showDsm]);

  // Handle overlay visibility change
  const handleOverlayVisibilityChange = useCallback((id: string, visible: boolean) => {
    if (id === 'ortho') {
      setShowOrtho(visible);
    } else if (id === 'dsm') {
      setShowDsm(visible);
    }
  }, []);

  // Build media types config
  const mediaTypesConfig: MediaTypeConfig[] = useMemo(() => {
    return [
      { id: 'projectPhotos' as MediaType, label: 'Project Photos', icon: 'photo', count: photos.length, visible: true },
      { id: 'hdImages' as MediaType, label: 'HD Images', icon: 'hd-image', count: 0, visible: false },
      { id: '360Images' as MediaType, label: '360 Images', icon: '360', count: 0, visible: false },
      { id: '3dModels' as MediaType, label: '3D Models', icon: '3d', count: 0, visible: false },
      { id: 'pointClouds' as MediaType, label: 'Point Clouds', icon: 'pointcloud', count: 0, visible: false },
      { id: 'floorTours' as MediaType, label: 'Floor Tours', icon: 'floor', count: 0, visible: false },
      { id: 'videos' as MediaType, label: 'Videos', icon: 'video', count: 0, visible: false },
      { id: 'sketches' as MediaType, label: 'Sketches', icon: 'sketch', count: 0, visible: false },
    ];
  }, [photos.length]);

  // Handle media type toggle
  const handleMediaTypeToggle = useCallback((id: MediaType) => {
    if (id === 'projectPhotos') {
      setShowPhotoPanel(!showPhotoPanel);
    }
    // Other media types can be handled here in the future
  }, [showPhotoPanel]);

  // Note: handleRightPanelMediaToggle removed - using simple layer panel

  // ============================================================
  // END HOOKS SECTION
  // ============================================================

  // Setup screen — only show if no data AND no project location to center on
  if (!hasData && !project?.latitude && !project?.longitude && !project?.address && !project?.city) {
    return <SetupView />;
  }

  return (
    <div className="viewer">
      <div className={`viewer__content geotiff-viewer__container ${placingPhotoMode ? 'geotiff-viewer__container--placing' : ''}`}>
        {/* Left Sidebar — hidden on mobile, shown via toggle button */}
        {(!isMobile || mobileSidebarOpen) && (
          <LeftSidebar
            basemap={basemap}
            onBasemapChange={setBasemap}
            overlays={overlayItems}
            onOverlayVisibilityChange={handleOverlayVisibilityChange}
            mediaTypes={mediaTypesConfig}
            onMediaTypeToggle={handleMediaTypeToggle}
            floorPlans={floorPlans}
            onAddFloorPlan={() => setFloorPlanModalOpen(true)}
            onFloorPlanVisibilityChange={handleFloorPlanVisibilityChange}
            onFloorPlanOpacityChange={handleFloorPlanOpacityChange}
            onFloorPlanEdit={handleFloorPlanEdit}
            onFloorPlanDelete={handleFloorPlanDelete}
            onMeasureDistance={() => measureMode === 'distance' ? finishMeasurement() : startMeasurement('distance')}
            onMeasureArea={() => measureMode === 'area' ? finishMeasurement() : startMeasurement('area')}
            activeTool={
              measureMode === 'distance' ? 'measure-distance' :
              measureMode === 'area' ? 'measure-area' :
              undefined
            }
          />
        )}

        {/* Mobile backdrop to close sidebar */}
        {isMobile && mobileSidebarOpen && (
          <div
            onClick={() => setMobileSidebarOpen(false)}
            style={{
              position: 'absolute',
              inset: 0,
              zIndex: 90,
              background: 'rgba(0,0,0,0.4)',
            }}
          />
        )}
        <div ref={containerRef} className="geotiff-viewer__map" />

        {/* Loading indicator */}
        {isLoading && (
          <div className="viewer__loading">
            <div className="viewer__loading-spinner" />
            <span className="viewer__loading-text">Loading...</span>
          </div>
        )}

        {/* Measurement hint */}
        {measureMode !== 'none' && (
          <div className="geotiff-viewer__measure-hint">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              {measureMode === 'distance' ? (
                <path d="M2 12h20M2 12l4-4M2 12l4 4M22 12l-4-4M22 12l-4 4" />
              ) : (
                <path d="M3 3h18v18H3z" />
              )}
            </svg>
            Click to add points. Double-click to finish.
            ({measurePoints.length} points)
          </div>
        )}

        {/* Photo Panel - hidden when right panel or sidebar is shown */}
        {showPhotoPanel && photos.length > 0 && !showLayerPanel && !photoSidebarOpen && (
          <PhotoPanel
            photos={photos}
            customCategories={customCategories}
            selectedPhotoId={selectedPhotoId}
            onSelectPhoto={setSelectedPhotoId}
            onEditPhoto={setEditingPhoto}
            onDeletePhoto={handleDeletePhoto}
            onFlyToPhoto={handleFlyToPhoto}
            onViewPhoto={openPhotoPreview}
            onManageCategories={() => setCategoryManagerOpen(true)}
            onAddPhotoAtLocation={(coordinates) => {
              setAddPhotoModalCoordinates(coordinates);
              setAddPhotoModalOpen(true);
            }}
          />
        )}

        {/* Right Info Panel (dark theme matching left sidebar) - hidden when sidebar is open */}
        {showLayerPanel && !photoSidebarOpen && (
          <RightInfoPanel
            projectName={project?.name || 'Untitled Project'}
            address={project?.address}
            mediaTypes={[
              { id: 'hd-images', label: 'HD Images', icon: 'hd-image', count: 0, active: visibleMediaTypes.has('hd-images') },
              { id: 'photos', label: 'Photos', icon: 'photo', count: photos.length, active: visibleMediaTypes.has('photos') },
              { id: '360-images', label: '360 Images', icon: '360', count: 0, active: visibleMediaTypes.has('360-images') },
              { id: 'videos', label: 'Videos', icon: 'video', count: 0, active: visibleMediaTypes.has('videos') },
              { id: '3d-models', label: '3D Models', icon: '3d', count: 0, active: visibleMediaTypes.has('3d-models') },
              { id: 'vr-tours', label: 'VR Tours', icon: 'vr-tour', count: 0, active: visibleMediaTypes.has('vr-tours') },
              { id: 'poi', label: 'Points of Interest', icon: 'poi', count: 0, active: visibleMediaTypes.has('poi') },
              { id: 'aoi', label: 'Areas of Interest', icon: 'aoi', count: 0, active: visibleMediaTypes.has('aoi') },
            ]}
            onMediaTypeToggle={(id) => {
              setVisibleMediaTypes(prev => {
                const newSet = new Set(prev);
                if (newSet.has(id)) {
                  newSet.delete(id);
                } else {
                  newSet.add(id);
                }
                return newSet;
              });
              // Toggle photo panel visibility when photos is toggled
              if (id === 'photos') {
                setShowPhotoPanel(prev => !prev);
              }
            }}
            layers={[
              ...(orthoTileCache ? [{
                id: 'ortho',
                name: 'Orthomosaic',
                visible: showOrtho,
                opacity: orthoOpacity,
                type: 'overlay' as const,
              }] : []),
              ...(dsmTileCache ? [{
                id: 'dsm',
                name: 'DSM',
                visible: showDsm,
                opacity: dsmOpacity,
                type: 'overlay' as const,
              }] : []),
            ]}
            onLayerToggle={(id) => {
              if (id === 'ortho') setShowOrtho(prev => !prev);
              if (id === 'dsm') setShowDsm(prev => !prev);
            }}
            onLayerOpacityChange={(id, opacity) => {
              if (id === 'ortho') setOrthoOpacity(opacity);
              if (id === 'dsm') setDsmOpacity(opacity);
            }}
            measurements={measurements}
            onClearMeasurements={clearAllMeasurements}
            onRemoveMeasurement={(id) => setMeasurements(prev => prev.filter(m => m.id !== id))}
            onClose={() => setShowLayerPanel(false)}
          />
        )}

        {/* Inline Photo Sidebar (for map marker clicks) */}
        <PhotoSidebar
          open={photoSidebarOpen}
          photos={sidebarPhotos}
          allPhotos={photos}
          initialPhotoId={sidebarInitialPhotoId}
          navCounter={sidebarNavCounter}
          customCategories={customCategories}
          onClose={() => setPhotoSidebarOpen(false)}
          onEditPhoto={(photo) => {
            setPhotoSidebarOpen(false);
            setEditingPhoto(photo);
          }}
          onDeletePhoto={(photoId) => {
            handleDeletePhoto(photoId);
            if (sidebarPhotos.length <= 1) {
              setPhotoSidebarOpen(false);
            } else {
              setSidebarPhotos(prev => prev.filter(p => p.id !== photoId));
            }
          }}
          onNavigateToPhoto={(photo) => {
            // Navigate to a photo in a different cluster
            setSidebarPhotos([photo]);
            setSidebarInitialPhotoId(photo.id);
            setSelectedPhotoId(photo.id);
          }}
        />

        {/* Placing Photo Mode Indicator */}
        {placingPhotoMode && (
          <div className="geotiff-viewer__placing-mode">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
              <circle cx="12" cy="10" r="3" />
            </svg>
            <span>Click on the map to place the photo</span>
            <button
              onClick={() => {
                setPlacingPhotoMode(false);
                setPendingPhotoData(null);
              }}
              className="geotiff-viewer__placing-mode-cancel"
            >
              Cancel (Esc)
            </button>
          </div>
        )}

        {/* Cursor info — hidden on mobile (no hover cursor) */}
        {cursorInfo && !placingPhotoMode && !isMobile && (
          <div className="viewer__info">
            <span className="viewer__info-label">Lng:</span>
            <span className="viewer__info-value">{cursorInfo.lng.toFixed(6)}</span>
            <span className="viewer__info-label" style={{ marginLeft: '12px' }}>Lat:</span>
            <span className="viewer__info-value">{cursorInfo.lat.toFixed(6)}</span>
            {cursorInfo.x !== undefined && cursorInfo.y !== undefined && (
              <>
                <span className="viewer__info-label" style={{ marginLeft: '12px' }}>Pixel:</span>
                <span className="viewer__info-value">{cursorInfo.x}, {cursorInfo.y}</span>
              </>
            )}
          </div>
        )}

        {/* Mobile sidebar toggle button */}
        {isMobile && !mobileSidebarOpen && (
          <button
            className="geotiff-viewer__mobile-toggle"
            onClick={() => setMobileSidebarOpen(true)}
            title="Open layers panel"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 2L2 7l10 5 10-5-10-5z" />
              <path d="M2 17l10 5 10-5" />
              <path d="M2 12l10 5 10-5" />
            </svg>
          </button>
        )}
      </div>

      {/* Add Photo Modal */}
      <AddPhotoModal
        isOpen={addPhotoModalOpen}
        onClose={() => {
          setAddPhotoModalOpen(false);
          setAddPhotoModalCoordinates(undefined);
        }}
        onAdd={handleAddPhoto}
        onPlaceOnMap={handleEnterPlacingMode}
        customCategories={customCategories}
        defaultCoordinates={addPhotoModalCoordinates || (cursorInfo ? { longitude: cursorInfo.lng, latitude: cursorInfo.lat } : undefined)}
      />

      {/* Edit Photo Modal */}
      {editingPhoto && (
        <AddPhotoModal
          isOpen={true}
          onClose={() => setEditingPhoto(null)}
          onAdd={(photoData) => handleUpdatePhoto({ ...editingPhoto, ...photoData })}
          onPlaceOnMap={(photoData) => {
            // Enter place-on-map mode for editing
            setEditingPhoto(null);
            setPendingPhotoData({ ...photoData, _editingId: editingPhoto.id } as any);
            setPlacingPhotoMode(true);
            setStatus('Click on the map to place the photo');
          }}
          customCategories={customCategories}
          editingPhoto={editingPhoto}
        />
      )}

      {/* Category Manager Modal */}
      <CategoryManagerModal
        isOpen={categoryManagerOpen}
        onClose={() => setCategoryManagerOpen(false)}
        customCategories={customCategories}
        onSave={handleSaveCategories}
        photosUsingCategory={photosUsingCategory}
      />

      {/* Photo Preview Modal */}
      <PhotoPreviewModal
        isOpen={photoPreviewOpen}
        photos={previewPhotos}
        allPhotos={photos}
        initialPhotoId={previewInitialPhotoId}
        customCategories={customCategories}
        onClose={() => setPhotoPreviewOpen(false)}
        onEditPhoto={(photo) => {
          setPhotoPreviewOpen(false);
          setEditingPhoto(photo);
        }}
        onDeletePhoto={(photoId) => {
          handleDeletePhoto(photoId);
          // If this was the last photo in the preview, close the modal
          if (previewPhotos.length <= 1) {
            setPhotoPreviewOpen(false);
          } else {
            // Update the preview photos list
            setPreviewPhotos(prev => prev.filter(p => p.id !== photoId));
          }
        }}
        onNavigateToPhoto={(photo) => {
          // Navigate to a photo in a different cluster
          setPreviewPhotos([photo]);
          setPreviewInitialPhotoId(photo.id);
          setSelectedPhotoId(photo.id);
        }}
      />

      {/* Add Floor Plan Modal */}
      <AddFloorPlanModal
        isOpen={floorPlanModalOpen}
        onClose={() => setFloorPlanModalOpen(false)}
        onAdd={handleAddFloorPlan}
        defaultCenter={getMapCenter()}
      />

      {/* Floor Plan Alignment Tool */}
      {aligningFloorPlan && viewerRef.current && (
        <FloorPlanAligner
          viewer={viewerRef.current}
          imageUrl={`/api/tiles/${aligningFloorPlan.tileCache}/0/0/0.${aligningFloorPlan.tileMetadata?.format || 'webp'}`}
          imageWidth={aligningFloorPlan.imageWidth || aligningFloorPlan.tileMetadata?.originalWidth || 1000}
          imageHeight={aligningFloorPlan.imageHeight || aligningFloorPlan.tileMetadata?.originalHeight || 1000}
          initialTransform={aligningFloorPlan.transform}
          onConfirm={handleFloorPlanAlignmentConfirm}
          onCancel={handleFloorPlanAlignmentCancel}
        />
      )}
    </div>
  );
}
